import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Lock, Eye, EyeOff, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getLiturgicalSeason, getSeasonLogo } from "@/lib/liturgicalCalendar";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [season] = useState(() => getLiturgicalSeason());

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }
      
      try {
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${apiUrl}/api/auth/verify-reset-token/${token}`);
        
        if (response.ok) {
          setTokenValid(true);
        }
      } catch (err) {
        console.error("Token verification error:", err);
      } finally {
        setVerifying(false);
      }
    };
    
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setLoading(true);
    
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess(true);
        toast.success("Password reset successfully!");
      } else {
        toast.error(data.detail || "Failed to reset password. Please try again.");
      }
    } catch (err) {
      console.error("Reset password error:", err);
      toast.error("Network error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  // Loading state while verifying token
  if (verifying) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center px-4 py-12">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#6B665E]" />
          <p className="mt-4 text-[#6B665E] font-['Nunito']">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // Invalid or missing token
  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md animate-fade-in">
          <div className="flex items-center justify-center gap-3 mb-10">
            <img 
              src={getSeasonLogo(season)} 
              alt="Parish" 
              className="w-11 h-11 rounded-lg transition-all duration-500"
            />
            <span className="font-['Crimson_Text'] text-3xl font-bold text-[#2D2A26] tracking-tight">Parish</span>
          </div>

          <Card className="bg-white border-[#E5E0D5] shadow-lg">
            <CardContent className="pt-6 text-center space-y-4">
              <XCircle className="w-16 h-16 text-red-500 mx-auto" />
              <h2 className="font-['Crimson_Text'] text-xl text-[#2D2A26]">Invalid Reset Link</h2>
              <p className="text-[#6B665E] font-['Nunito']">
                This password reset link is invalid or has expired.
              </p>
              <div className="pt-4 space-y-2">
                <Link to="/forgot-password">
                  <Button 
                    className="w-full text-white font-['Nunito']"
                    style={{ backgroundColor: season.colors.primary }}
                  >
                    Request New Reset Link
                  </Button>
                </Link>
                <Link 
                  to="/login" 
                  className="block text-sm font-['Nunito'] hover:underline pt-2"
                  style={{ color: season.colors.primary }}
                >
                  Back to Sign In
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md animate-fade-in">
          <div className="flex items-center justify-center gap-3 mb-10">
            <img 
              src={getSeasonLogo(season)} 
              alt="Parish" 
              className="w-11 h-11 rounded-lg transition-all duration-500"
            />
            <span className="font-['Crimson_Text'] text-3xl font-bold text-[#2D2A26] tracking-tight">Parish</span>
          </div>

          <Card className="bg-white border-[#E5E0D5] shadow-lg">
            <CardContent className="pt-6 text-center space-y-4">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
              <h2 className="font-['Crimson_Text'] text-xl text-[#2D2A26]">Password Reset Successful</h2>
              <p className="text-[#6B665E] font-['Nunito']">
                Your password has been updated. You can now sign in with your new password.
              </p>
              <div className="pt-4">
                <Link to="/login">
                  <Button 
                    className="w-full text-white font-['Nunito']"
                    style={{ backgroundColor: season.colors.primary }}
                  >
                    Sign In
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md animate-fade-in">
        <div className="flex items-center justify-center gap-3 mb-10">
          <img 
            src={getSeasonLogo(season)} 
            alt="Parish" 
            className="w-11 h-11 rounded-lg transition-all duration-500"
          />
          <span className="font-['Crimson_Text'] text-3xl font-bold text-[#2D2A26] tracking-tight">Parish</span>
        </div>

        <Card className="bg-white border-[#E5E0D5] shadow-lg" data-testid="reset-password-card">
          <CardHeader className="pb-4">
            <CardTitle className="font-['Crimson_Text'] text-2xl text-[#2D2A26]">
              Create New Password
            </CardTitle>
            <CardDescription className="font-['Nunito'] text-[#6B665E]">
              Enter your new password below
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="font-['Nunito'] text-[#2D2A26] text-sm">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-[#6B665E]" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 border-[#E5E0D5] font-['Nunito']"
                    data-testid="new-password-input"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-[#6B665E] hover:text-[#2D2A26] transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="font-['Nunito'] text-[#2D2A26] text-sm">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-[#6B665E]" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 border-[#E5E0D5] font-['Nunito']"
                    data-testid="confirm-password-input"
                    required
                  />
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-xs text-red-500 font-['Nunito']">Passwords don't match</p>
                )}
              </div>
              
              <Button
                type="submit"
                className="w-full text-white font-['Nunito'] font-semibold h-11 active:scale-95 transition-all"
                style={{ backgroundColor: season.colors.primary }}
                disabled={loading || password !== confirmPassword}
                data-testid="reset-password-submit-btn"
              >
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
